import { get } from "lodash"
import dynamicManifest from "@/content/content-paths.dynamic.json"
import { contentMapBody } from "@/utils/contentMapBody"
import type { ContentMapEntry } from "@/utils/contentMapTypes"

export type ContentMapSourceKind = NonNullable<ContentMapEntry["sourceKind"]>

export type DynamicContentManifest = {
  routes?: {
    paths?: string[]
    reason?: string
    source?: string
    appRoutes?: string[]
  }[]
  routePatterns?: {
    pattern: string
    reason?: string
    source?: string
    appRoutes?: string[]
  }[]
}

export type ManifestResolvedPath = {
  path: string
  reason: string
  source: string
  sourceKind: "manifest" | "manifest_pattern"
}

const EXCLUDED_TOP_LEVEL_KEYS = new Set(["links", "mappings", "csv_data"])

function isStandardPageContent(obj: Record<string, unknown>): boolean {
  return "contentBody" in obj || "page_contents" in obj
}

function shouldSkipContentBranch(prefix: string, key: string): boolean {
  if (prefix === "" && EXCLUDED_TOP_LEVEL_KEYS.has(key)) return true
  if (key === "page_contents") return true
  return false
}

/** Walks memberOnlineContent and returns dot-paths for CMS leaf assets. */
export function flattenMatrixLeafPaths(matrixContent: unknown): Set<string> {
  const paths = new Set<string>()

  function walk(obj: unknown, prefix: string) {
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) return

    const record = obj as Record<string, unknown>
    if ("assetId" in record) {
      if (isStandardPageContent(record) && prefix) {
        paths.add(prefix)
      }
      return
    }

    for (const [key, value] of Object.entries(record)) {
      if (shouldSkipContentBranch(prefix, key)) continue
      const nextPrefix = prefix ? `${prefix}.${key}` : key
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        walk(value, nextPrefix)
      }
    }
  }

  walk(matrixContent, "")
  return paths
}

export function routePatternToRegExp(pattern: string): RegExp {
  const escaped = pattern
    .split("*")
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("[^.]+")
  return new RegExp(`^${escaped}$`)
}

export function matchRoutesByPattern(
  pattern: string,
  knownRoutes: Iterable<string>
): string[] {
  const re = routePatternToRegExp(pattern)
  return [...knownRoutes].filter((route) => re.test(route))
}

/** Normalises router pathname for manifest appRoutes matching. */
export function normalizeAppPathname(pathname: string): string {
  const withoutHash = pathname.split("#")[0] ?? pathname
  if (!withoutHash || withoutHash === "/") return "/"
  return withoutHash.endsWith("/") && withoutHash.length > 1
    ? withoutHash.slice(0, -1)
    : withoutHash
}

export function matchesAppRoute(pathname: string, appRoute: string): boolean {
  const current = normalizeAppPathname(pathname)
  const route = normalizeAppPathname(appRoute)
  if (current === route) return true
  return current.startsWith(`${route}/`)
}

function routeEntryMatches(
  pathname: string,
  appRoutes: string[] | undefined
): boolean {
  if (!appRoutes?.length) return true
  return appRoutes.some((route) => matchesAppRoute(pathname, route))
}

export function resolveManifestPathsForRoute(
  pathname: string,
  manifest: DynamicContentManifest,
  knownRoutes: Set<string>
): ManifestResolvedPath[] {
  const byPath = new Map<string, ManifestResolvedPath>()

  for (const entry of manifest.routes ?? []) {
    if (!routeEntryMatches(pathname, entry.appRoutes)) continue
    for (const path of entry.paths ?? []) {
      if (!knownRoutes.has(path)) continue
      byPath.set(path, {
        path,
        reason: entry.reason ?? "Dynamic manifest route",
        source: entry.source ?? "content-paths.dynamic.json",
        sourceKind: "manifest",
      })
    }
  }

  for (const entry of manifest.routePatterns ?? []) {
    if (!routeEntryMatches(pathname, entry.appRoutes)) continue
    for (const path of matchRoutesByPattern(entry.pattern, knownRoutes)) {
      byPath.set(path, {
        path,
        reason: entry.reason ?? `Pattern ${entry.pattern}`,
        source: entry.source ?? "content-paths.dynamic.json",
        sourceKind: "manifest_pattern",
      })
    }
  }

  return [...byPath.values()].sort((a, b) => a.path.localeCompare(b.path))
}

export function buildManifestCandidateEntry(
  candidate: ManifestResolvedPath,
  matrixContent: unknown
): Omit<ContentMapEntry, "refId"> {
  const content = get(matrixContent, candidate.path) as
    | Record<string, unknown>
    | undefined

  return {
    path: candidate.path,
    assetId: (content?.assetId as string | undefined) ?? "",
    prodAssetId: (content?.prodAssetId as string | undefined) ?? "",
    contentLengthMatch: content?.contentLengthMatch as boolean | undefined,
    contentBody: content ? contentMapBody(content) : "",
    updated: (content?.updated as string | undefined) ?? "",
    failed: !content,
    sourceKind: candidate.sourceKind,
    manifestReason: candidate.reason,
    manifestSource: candidate.source,
  }
}

export const contentMapDynamicManifest =
  dynamicManifest as DynamicContentManifest
